import { Mediator, app } from "modular-openscriptjs";
import { inform, eParse, processStart, processEnd } from "../../helpers";
import { STRINGS, api } from "../../declarations";
import { analyze } from "../../analytics";
import {
    NativePurchases,
    PURCHASE_TYPE,
} from "@capgo/native-purchases";
import {
    appStoreProductIdForCredits,
    availableAppStorePackages,
} from "../../appStoreProducts";

const broker = app("broker");
const $e = app("$e");

/**
 * Buys AI Credits through Apple In-App Purchase. Apple requires this for
 * any in-app virtual currency on iOS (App Review Guideline 3.1.1) - no
 * other payment method is allowed here, so AiCredit.getPayment() routes
 * straight to this mediator instead of the normal payments page whenever
 * the app is running natively on iOS.
 */
export default class AppStorePayment extends Mediator {
    constructor() {
        super();

        let event = {
            payment: {
                method: {
                    appstore: {
                        process: true,
                    },
                },
            },
        };

        broker.registerEvents(event);
    }

    $$payment = {
        method: {
            appstore: {
                process: (ed, event) => this.processPayment(ed, event),
            },
        },
    };

    async processPayment(ed, event) {
        let { meta, message } = eParse(ed);

        const credits = message.get("credits");

        if (!availableAppStorePackages().includes(credits)) {
            inform(
                `AI Credit purchases of ${credits} credits aren't available on iOS yet`,
                STRINGS.ERROR,
            );
            return this.broadcast($e.payment.has.processingError, ed);
        }

        const productId = appStoreProductIdForCredits(credits);

        processStart();

        let transaction;

        try {
            transaction = await NativePurchases.purchaseProduct({
                productIdentifier: productId,
                productType: PURCHASE_TYPE.INAPP,
                isConsumable: true,
            });
        } catch (error) {
            processEnd();

            // The user backing out of the Apple purchase sheet also lands
            // here - that's normal, not an error worth alarming anyone with.
            if (!this.isUserCancelled(error)) {
                inform(
                    "The App Store purchase did not go through",
                    STRINGS.ERROR,
                );
            }

            return this.broadcast($e.payment.has.processingError, ed);
        }

        let res = await api.post(`${meta.get("route")}`, {
            method: "appstore",
            information: {
                transaction_id: transaction.transactionId,
                product_id: transaction.productIdentifier ?? productId,
            },
            amount: message.get("amount"),
        });

        if (!res) {
            processEnd();
            return this.broadcast($e.payment.has.processingError, ed);
        }

        inform(res.message, STRINGS.SUCCESS);

        analyze("payment_made", {
            amount: message.get("amount"),
            currency: "KES",
            method: "appstore",
        });

        return this.broadcast($e.payment.completed, ed);
    }

    isUserCancelled(error) {
        const message = (error?.message || "").toLowerCase();
        return (
            message.includes("cancel") ||
            error?.code === "cancelled" ||
            error?.code === "userCancelled"
        );
    }
}
